import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CheckCircle2, ShoppingCart, Leaf, Droplets, Shovel, Sun, Package, Printer, Download, Save, Cloud, CloudOff, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sanitize text for safe insertion
const sanitize = (text) => DOMPurify.sanitize(text || '', { ALLOWED_TAGS: [] });

const GetStartedChecklist = ({ gardenType = "vegetable", compact = false, user = null }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedFromServer, setHasLoadedFromServer] = useState(false);

  // Load saved progress from server on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/checklist-progress`, {
          withCredentials: true
        });
        
        if (response.data.saved && response.data.checked_items) {
          setCheckedItems(response.data.checked_items);
          setIsSynced(true);
          setLastSaved(response.data.last_updated);
        }
        setHasLoadedFromServer(true);
      } catch (error) {
        console.error("Failed to load checklist progress:", error);
        setHasLoadedFromServer(true);
      }
    };

    loadProgress();
  }, [user]);

  // Save progress to server (debounced)
  const saveProgress = useCallback(async (items) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/checklist-progress`, {
        checked_items: items
      }, {
        withCredentials: true
      });
      setIsSynced(true);
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error("Failed to save checklist progress:", error);
      setIsSynced(false);
    } finally {
      setIsSaving(false);
    }
  }, [user]);

  // Debounced save when items change
  useEffect(() => {
    if (!user || !hasLoadedFromServer) return;
    
    const timer = setTimeout(() => {
      saveProgress(checkedItems);
    }, 1000); // Save after 1 second of no changes

    return () => clearTimeout(timer);
  }, [checkedItems, user, hasLoadedFromServer, saveProgress]);

  const toggleItem = (id) => {
    setCheckedItems(prev => {
      const newItems = { ...prev, [id]: !prev[id] };
      setIsSynced(false); // Mark as unsaved
      return newItems;
    });
  };

  // Essential items needed to start gardening today
  const starterItems = {
    basic: [
      { id: "soil", name: "Quality Potting Soil or Compost", icon: Shovel, category: "Foundation", tip: "Look for organic options with good drainage" },
      { id: "containers", name: "Containers or Raised Bed", icon: Package, category: "Foundation", tip: "Start with 5-gallon buckets or fabric grow bags" },
      { id: "seeds", name: "Seeds or Seedlings", icon: Leaf, category: "Plants", tip: "Start with easy growers: lettuce, herbs, tomatoes" },
      { id: "water", name: "Watering Can or Hose", icon: Droplets, category: "Watering", tip: "A simple watering can works great for small gardens" },
      { id: "sun", name: "6+ Hours of Sunlight", icon: Sun, category: "Location", tip: "Most vegetables need full sun" },
    ],
    tools: [
      { id: "gloves", name: "Garden Gloves", icon: Package, category: "Tools", tip: "Protect your hands while working" },
      { id: "trowel", name: "Hand Trowel", icon: Shovel, category: "Tools", tip: "Essential for transplanting and weeding" },
      { id: "pruners", name: "Pruning Shears", icon: Package, category: "Tools", tip: "For harvesting and maintenance" },
    ],
    optional: [
      { id: "fertilizer", name: "Organic Fertilizer", icon: Leaf, category: "Nutrition", tip: "Fish emulsion or compost tea work well" },
      { id: "mulch", name: "Mulch or Straw", icon: Package, category: "Protection", tip: "Retains moisture and prevents weeds" },
      { id: "stakes", name: "Stakes or Cages", icon: Package, category: "Support", tip: "For tomatoes, peppers, and climbing plants" },
    ]
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalItems = [...starterItems.basic, ...starterItems.tools].length;
  const progress = Math.round((checkedCount / totalItems) * 100);

  // Export as printable shopping list
  const exportShoppingList = () => {
    const allItems = [...starterItems.basic, ...starterItems.tools, ...starterItems.optional];
    const uncheckedItems = allItems.filter(item => !checkedItems[item.id]);
    
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Garden Shopping List - RootSpace</title>
  <style>
    body { font-family: Georgia, serif; max-width: 600px; margin: 40px auto; padding: 20px; }
    h1 { color: #2d5016; border-bottom: 2px solid #2d5016; padding-bottom: 10px; }
    h2 { color: #4a7c23; margin-top: 30px; }
    .item { display: flex; align-items: flex-start; margin: 15px 0; }
    .checkbox { width: 20px; height: 20px; border: 2px solid #2d5016; margin-right: 15px; flex-shrink: 0; }
    .checked { background: #2d5016; }
    .item-name { font-weight: bold; }
    .item-tip { color: #666; font-size: 0.9em; margin-left: 35px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; color: #666; font-size: 0.8em; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>🌱 Garden Shopping List</h1>
  <p>Your personalized list from RootSpace - ${new Date().toLocaleDateString()}</p>
  
  <h2>Essential Items</h2>
  ${starterItems.basic.map(item => `
    <div class="item">
      <div class="checkbox ${checkedItems[item.id] ? 'checked' : ''}"></div>
      <div>
        <div class="item-name">${item.name}</div>
        <div class="item-tip">💡 ${item.tip}</div>
      </div>
    </div>
  `).join('')}
  
  <h2>Basic Tools</h2>
  ${starterItems.tools.map(item => `
    <div class="item">
      <div class="checkbox ${checkedItems[item.id] ? 'checked' : ''}"></div>
      <div>
        <div class="item-name">${item.name}</div>
        <div class="item-tip">💡 ${item.tip}</div>
      </div>
    </div>
  `).join('')}
  
  <h2>Optional Upgrades</h2>
  ${starterItems.optional.map(item => `
    <div class="item">
      <div class="checkbox ${checkedItems[item.id] ? 'checked' : ''}"></div>
      <div>
        <div class="item-name">${item.name}</div>
        <div class="item-tip">💡 ${item.tip}</div>
      </div>
    </div>
  `).join('')}
  
  <div class="footer">
    <p>✅ ${checkedCount} items ready | 📋 ${totalItems - checkedCount} items to get</p>
    <p>Generated by RootSpace.pro - Happy Gardening! 🌿</p>
  </div>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Download as text file
  const downloadShoppingList = () => {
    const allItems = [...starterItems.basic, ...starterItems.tools, ...starterItems.optional];
    
    let content = `GARDEN SHOPPING LIST - RootSpace.pro
Generated: ${new Date().toLocaleDateString()}
=====================================

ESSENTIAL ITEMS:
${starterItems.basic.map(item => `${checkedItems[item.id] ? '✅' : '⬜'} ${item.name}
   Tip: ${item.tip}`).join('\n\n')}

BASIC TOOLS:
${starterItems.tools.map(item => `${checkedItems[item.id] ? '✅' : '⬜'} ${item.name}
   Tip: ${item.tip}`).join('\n\n')}

OPTIONAL UPGRADES:
${starterItems.optional.map(item => `${checkedItems[item.id] ? '✅' : '⬜'} ${item.name}
   Tip: ${item.tip}`).join('\n\n')}

=====================================
Progress: ${checkedCount}/${totalItems} items ready (${progress}%)
Happy Gardening! 🌱
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'garden-shopping-list.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (compact) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-green-700">
            <ShoppingCart className="w-4 h-4" />
            Get Out and Plant Today!
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-1.5">
            {starterItems.basic.slice(0, 5).map(item => {
              const Icon = item.icon;
              return (
                <Badge 
                  key={item.id}
                  variant={checkedItems[item.id] ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${checkedItems[item.id] ? 'bg-green-600' : 'hover:bg-green-100'}`}
                  onClick={() => toggleItem(item.id)}
                >
                  <Icon className="w-3 h-3 mr-1" />
                  {item.name.split(' ')[0]}
                  {checkedItems[item.id] && <CheckCircle2 className="w-3 h-3 ml-1" />}
                </Badge>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {checkedCount}/{totalItems} items ready • {progress}% prepared
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-amber-50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-green-700">
            <ShoppingCart className="w-5 h-5" />
            Get Out and Plant Today!
          </CardTitle>
          {/* Sync Status Indicator */}
          {user && (
            <div className="flex items-center gap-2" data-testid="checklist-sync-status">
              {isSaving ? (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              ) : isSynced ? (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <Cloud className="w-3 h-3" />
                  Saved
                </span>
              ) : (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <CloudOff className="w-3 h-3" />
                  Unsaved
                </span>
              )}
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Everything you need to start your garden right now
          {user && <span className="text-green-600 ml-1">• Progress auto-saves to your account</span>}
          {!user && <span className="text-amber-600 ml-1">• Sign in to save progress</span>}
        </p>
        {/* Progress bar */}
        <div className="mt-2">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {checkedCount} of {totalItems} items ready ({progress}% prepared)
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Essential Items */}
        <div>
          <h4 className="font-semibold text-sm text-green-700 mb-2 flex items-center gap-1">
            <Leaf className="w-4 h-4" /> Essential Items
          </h4>
          <div className="space-y-2">
            {starterItems.basic.map(item => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                    checkedItems[item.id] 
                      ? 'bg-green-100 border border-green-300' 
                      : 'bg-white border border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    checkedItems[item.id] ? 'bg-green-500 text-white' : 'bg-gray-100'
                  }`}>
                    {checkedItems[item.id] ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${checkedItems[item.id] ? 'line-through text-gray-500' : ''}`}>
                      {item.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.tip}</p>
                  </div>
                  <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tools */}
        <div>
          <h4 className="font-semibold text-sm text-amber-700 mb-2 flex items-center gap-1">
            <Shovel className="w-4 h-4" /> Basic Tools
          </h4>
          <div className="space-y-2">
            {starterItems.tools.map(item => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                    checkedItems[item.id] 
                      ? 'bg-amber-100 border border-amber-300' 
                      : 'bg-white border border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    checkedItems[item.id] ? 'bg-amber-500 text-white' : 'bg-gray-100'
                  }`}>
                    {checkedItems[item.id] ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${checkedItems[item.id] ? 'line-through text-gray-500' : ''}`}>
                      {item.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.tip}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional Upgrades */}
        <details className="group">
          <summary className="font-semibold text-sm text-purple-700 mb-2 flex items-center gap-1 cursor-pointer list-none">
            <Package className="w-4 h-4" /> Optional Upgrades
            <span className="text-xs text-muted-foreground ml-2">(click to expand)</span>
          </summary>
          <div className="space-y-2 mt-2">
            {starterItems.optional.map(item => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                    checkedItems[item.id] 
                      ? 'bg-purple-100 border border-purple-300' 
                      : 'bg-white border border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    checkedItems[item.id] ? 'bg-purple-500 text-white' : 'bg-gray-100'
                  }`}>
                    {checkedItems[item.id] ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${checkedItems[item.id] ? 'line-through text-gray-500' : ''}`}>
                      {item.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.tip}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </details>

        {/* Ready message */}
        {progress >= 80 && (
          <div className="p-3 bg-green-100 border border-green-300 rounded-lg text-center">
            <p className="text-green-700 font-medium">
              You&apos;re ready to start gardening! 🌱
            </p>
          </div>
        )}

        {/* Export Actions */}
        <div className="flex gap-2 pt-4 border-t mt-4">
          <Button 
            variant="outline" 
            className="flex-1 gap-2"
            onClick={exportShoppingList}
            data-testid="checklist-print-btn"
          >
            <Printer className="w-4 h-4" />
            Print List
          </Button>
          <Button 
            variant="outline" 
            className="flex-1 gap-2"
            onClick={downloadShoppingList}
            data-testid="checklist-download-btn"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GetStartedChecklist;
